/**
 * Die farbigen Chips — Warnungen, Status, Belegart.
 *
 * Farbe ist hier eine Aussage, keine Verzierung: rot heisst „das zahlt niemand
 * ohne Anruf", gelb heisst „schau hin", gruen heisst „lief durch". Die
 * Zuordnung von Stufe zu Farbe steht in `lib/beleg.ts` und nicht hier — sonst
 * ist das dritte Rot ein anderes.
 *
 * Jeder Chip traegt zusaetzlich ein Wort. Wer rot-gruen nicht unterscheidet,
 * liest sonst nichts.
 */

import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';

import { ampelKlassen, warnstufeKlassen, WARNUNG_KURZ, type Ampel } from '@/lib/beleg';
import type { Warnung } from '@/types';

export function Chip({
  children,
  klassen,
  titel,
}: {
  children: ReactNode;
  klassen?: string;
  titel?: string;
}) {
  return (
    <span
      title={titel}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        klassen ?? 'border-slate-200 bg-slate-50 text-slate-600',
      )}
    >
      {children}
    </span>
  );
}

/** Ein Chip je Warnung — mit dem ausformulierten Satz des Servers als Titel. */
export function WarnungsChip({ warnung }: { warnung: Warnung }) {
  const Symbol = warnung.stufe === 'rot' ? ShieldAlert : AlertTriangle;
  return (
    <Chip klassen={warnstufeKlassen(warnung.stufe)} titel={warnung.text}>
      <Symbol size={12} />
      {WARNUNG_KURZ[warnung.schluessel] ?? warnung.schluessel}
    </Chip>
  );
}

/**
 * Die Ampel einer Listenzeile.
 *
 * In der Liste stehen nur Zaehler (`warnungen_rot`, `warnungen_gelb`) — die
 * ausgeschriebenen Warnungen kommen erst mit der Detailansicht. Deshalb zeigt
 * die Zeile die Farbe und die Zahl, nicht den Text.
 */
export function AmpelChip({ rot, gelb }: { rot: number; gelb: number }) {
  const ampel: Ampel = rot > 0 ? 'rot' : gelb > 0 ? 'gelb' : 'gruen';

  if (ampel === 'gruen') {
    return (
      <Chip klassen={ampelKlassen('gruen')} titel="Keine Warnung — lief durch">
        <CheckCircle2 size={12} />
        ohne Befund
      </Chip>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {rot > 0 && (
        <Chip
          klassen={ampelKlassen('rot')}
          titel={`${rot} rote Warnung${rot === 1 ? '' : 'en'} — Freigabe nur durch einen Admin, nach Rückruf`}
        >
          <ShieldAlert size={12} />
          {rot} rot
        </Chip>
      )}
      {gelb > 0 && (
        <Chip
          klassen={ampelKlassen('gelb')}
          titel={`${gelb} gelbe Warnung${gelb === 1 ? '' : 'en'} — bewusst freigeben`}
        >
          <AlertTriangle size={12} />
          {gelb} gelb
        </Chip>
      )}
    </span>
  );
}
